import { faCaretDown, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Label } from '@playcastdotio/Shadcn';
import clsx from 'clsx';
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { PlaycastContext } from '../PlaycastWrapper';
import { Source } from './Display';
import constants from '@playcastdotio/GlobalConfig';

type LocalProps = {
  choiceState: [string | null, Dispatch<SetStateAction<string | null>>];
};

const getDefaultSources = (): Source[] => {
  const baseSources: Source[] = [
    {
      label: 'Desktop',
      choices: [
        {
          label: 'Display',
          value: 'display',
        },
        {
          label: 'Surf Desktop',
          value: 'followMouse',
        },
      ],
    },
    {
      label: 'Window Capture',
      choices: [],
    },
    {
      label: 'Cameras',
      choices: [],
    },
    {
      label: 'Media',
      choices: [
        {
          label: 'Static Image',
          value: '1',
        },
      ],
    },
  ];

  // Add Desktop Capture option only in development
  if (constants.ENVIRONMENT === 'development') {
    baseSources[0].choices.push({
      label: 'Desktop Capture',
      value: 'desktopCapture',
    });
  }

  return baseSources;
};

export const DisplayPicker = ({ choiceState }: LocalProps) => {
  const context = useContext(PlaycastContext);
  const [isSourceOpen, setIsSourceOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState<Source['label'] | null>(null);
  const [selectedChoice, setSelectedChoice] = choiceState;
  const [selectedChoiceLabel, setSelectedChoiceLabel] = useState<string | null>(null);
  const [sources, setSources] = useState<Source[]>(getDefaultSources());
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update sources when cameras change
  useEffect(() => {
    if (context.stateStores.videoState.cameras.length > 0) {
      setSources((prev) => [
        {
          label: 'Desktop',
          choices: prev[0].choices,
        },
        {
          label: 'Window Capture',
          choices: prev[1].choices,
        },
        {
          label: 'Cameras',
          choices: context.stateStores.videoState.cameras.map((camera) => ({
            label: camera.displayName,
            value: camera.deviceId,
          })),
        },
        {
          label: 'Media',
          choices: prev[3].choices,
        },
      ]);
    }
  }, [context.stateStores.videoState.cameras]);

  useEffect(() => {
    setSources((prev) => {
      // Create a new array with all existing sources
      const newSources = prev.map((source) => {
        // Only update the Window Capture source
        if (source.label === 'Window Capture') {
          // If we have running applications, update the choices
          if (context.stateStores.runningApplicationState.length) {
            return {
              ...source,
              choices: context.stateStores.runningApplicationState.map((app) => ({
                label: app[0],
                value: app[1].processId.toString(),
              })),
            };
          }
          // If no running applications, keep existing choices
          return source;
        }
        // Keep all other sources unchanged
        return source;
      });
      return newSources;
    });
  }, [context.stateStores.runningApplicationState]);

  // Recalculate selected choice and label
  const recalculateSelectedChoice = useCallback(() => {
    // Skip recalculation if window capture is selected
    if (selectedSource === 'Window Capture') {
      return;
    }

    console.log('[DisplayPicker] Recalculating selectedChoice + selectedChoiceLabel');
    const selectedLabel = sources
      .flatMap((source) => source.choices)
      .find((choice) => choice.value === context.stateStores.videoState.selectedDevice)?.label;

    if (context.stateStores.videoState.selectedDevice === '0') {
      if (context.stateStores.videoState.enabled) {
        setSelectedChoice('display');
        setSelectedChoiceLabel('Display');
      } else {
        // show static image
        setSelectedChoice('1');
        setSelectedChoiceLabel('Static Image');
      }
    } else {
      setSelectedChoice(context.stateStores.videoState.selectedDevice);
      if (context.stateStores.videoState.selectedDevice === 'display' && context.stateStores.videoState.windowCapturePid) {
        // Find the window label from runningApplicationState
        const windowInfo = context.stateStores.runningApplicationState.find(
          (app) => app[1].processId.toString() === context.stateStores.videoState.windowCapturePid?.toString(),
        );
        if (windowInfo) {
          setSelectedChoiceLabel(windowInfo[0]);
        }
      } else if (selectedLabel) {
        setSelectedChoiceLabel(selectedLabel);
      }
    }
    console.log('[DisplayPicker] selectedChoice', selectedChoice, 'selectedChoiceLabel', selectedChoiceLabel);
  }, [
    context.stateStores.videoState.enabled,
    context.stateStores.videoState.selectedDevice,
    context.stateStores.videoState.windowCapturePid,
    context.stateStores.runningApplicationState,
    sources,
    setSelectedChoice,
    selectedSource,
  ]);

  // Sync selected choice and label when device or enabled state changes
  useEffect(() => {
    recalculateSelectedChoice();
  }, [context.stateStores.videoState.selectedDevice, context.stateStores.videoState.enabled, recalculateSelectedChoice]);

  // Initial recalculate on mount
  useEffect(() => {
    recalculateSelectedChoice();
  }, []);

  // Handle source toggle
  const handleSourceToggle = useCallback(() => {
    setIsSourceOpen((prev) => !prev);
    setTimeout(() => {
      dropdownRef.current?.focus();
    }, 0);

    // refresh running applications
    context.targetFunctions.processEnumRunning();
  }, [context.targetFunctions]);

  // Handle source change
  const handleSourceChange = useCallback((source: Source['label']) => {
    setSelectedSource((prevSource) => (source === prevSource ? null : source));
  }, []);

  // Handle selected choice
  const handleSelectedChoice = useCallback(
    (value: string) => {
      setSelectedChoice(value);
      handleSourceToggle();
      console.log('selectedChoice', value, 'selectedSource', selectedSource);
      const selectedChoiceLabel = sources.find((source) => source.label === selectedSource)?.choices.find((c) => c.value === value)?.label;
      if (selectedChoiceLabel) {
        setSelectedChoiceLabel(selectedChoiceLabel);
      }

      switch (selectedSource) {
        case 'Desktop':
          if (value === 'followMouse') {
            console.log('Follow Mouse source selected');
            context.targetFunctions.setDisplaySurfing(true);
          } else if (value === 'desktopCapture') {
            console.log('Desktop Capture source selected');
            context.targetFunctions.setDisplaySurfing(false);
          } else {
            console.log('Display source selected');
            context.targetFunctions.setDisplaySurfing(false);
          }
          context.targetFunctions.processDisconnect();
          context.targetFunctions.setOutboundVideoDevice(value);
          break;
        case 'Window Capture':
          try {
            context.targetFunctions.processSetPolicy(false, true, true, false, 60000, true, true, true, true);
            context.targetFunctions.processSelect(parseInt(value));
            context.targetFunctions.setOutboundVideoDevice('gameCapture', parseInt(value));
          } catch (e) {
            console.error('Error setting window capture policy:', e);
          }
          break;
        case 'Cameras':
          context.targetFunctions.processDisconnect();
          context.targetFunctions.setOutboundVideoDevice(value);
          break;
        case 'Media':
          console.log('Media source selected');
          context.targetFunctions.processDisconnect();
          context.targetFunctions.setOutboundVideoDevice('0');
          break;
        default:
          console.log('No source selected');
      }
    },
    [selectedSource, sources, context.targetFunctions, setSelectedChoice, handleSourceToggle],
  );

  return (
    <div className='relative'>
      <button
        className={clsx(
          'theme-midnight:shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] bg-primary hover:bg-primary/60 flex w-full cursor-pointer items-center justify-between rounded-md p-2',
        )}
        // onClick={handleSourceToggle}
        onMouseDown={handleSourceToggle}
      >
        <span className='truncate'>{selectedChoiceLabel ?? 'Source'}</span>
        <FontAwesomeIcon icon={faCaretDown} className='h-4 w-4 flex-shrink-0' />
      </button>
      <div
        ref={dropdownRef}
        tabIndex={0}
        className={clsx(
          isSourceOpen ? 'flex' : 'hidden',
          'absolute z-50 w-full translate-x-0 flex-col rounded-md bg-gray-600 shadow md:translate-x-1',
        )}
        onBlur={() => setIsSourceOpen(false)}
      >
        {sources.map((source) => (
          <div
            key={source.label}
            className='border-gray-500/60 first:rounded-t-md last:rounded-b-md [&:not(:last-child)]:border-b'
            onClick={() => handleSourceChange(source.label)}
          >
            <div className='flex w-full cursor-pointer items-center justify-between bg-inherit p-2'>
              <Label className={clsx(selectedSource === source.label ? 'text-amber-400' : 'text-midnight-white', 'text-md cursor-pointer')}>
                {source.label}
              </Label>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={clsx(selectedSource === source.label ? 'rotate-180' : 'rotate-0', 'h-3 w-3 transition-all')}
              />
            </div>
            <div
              className={clsx(
                source.label === selectedSource ? 'max-h-[200px] overflow-y-auto' : 'max-h-0 overflow-hidden',
                'bg-gray-300 transition-all ease-in-out',
              )}
            >
              {source.choices.map((choice, index) => (
                <button
                  key={choice.label}
                  className={clsx(
                    source.label === selectedSource ? 'translate-y-0' : '-translate-y-16',
                    'group flex w-full cursor-pointer items-center justify-between border-white bg-inherit p-1 text-gray-900 transition-all ease-in-out first:rounded-t-md last:rounded-b-md [&:not(:last-child)]:border-b',
                    index >= 4 ? 'border-t border-gray-400' : '',
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectedChoice(choice.value);
                  }}
                >
                  <Label className='group-hover:border-midnight w-full cursor-pointer truncate rounded-md border-2 border-gray-300 p-2 text-left text-sm font-normal group-hover:border-dashed'>
                    {choice.label}
                  </Label>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
